import { memo } from 'react';
import {
  ChoiceItalianVM,
  ForeignState,
  ItalianState,
} from 'mutation/view-models/registration-citizenship';
import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import ArticleList from '../shared/article-list';
import Italian from '../shared/italian';
import Foreign from '../shared/foreign';
import { FormikErrors, FormikTouched } from 'formik';
import { ItemVM } from 'shell/view-models/item';
import DocumentActForm from 'shell/components/shared/act';
import { MotArtCitizenship } from 'shell/models/mutation/mot-art-citizenship';

interface ChoiceItalianProps {
  values?: ChoiceItalianVM;
  onChange: (key: keyof ChoiceItalianVM, value: ChoiceItalianVM[keyof ChoiceItalianVM]) => void;
  onChangeValues: (values: ChoiceItalianVM) => void;
  errors?: FormikErrors<ChoiceItalianVM>;
  touched?: FormikTouched<ChoiceItalianVM>;
  idReason: string;
  disabled?: boolean;
}

const ChoiceItalianComponent = ({
  onChange,
  values,
  errors,
  touched,
  idReason,
  onChangeValues,
  disabled,
}: ChoiceItalianProps): React.ReactElement => {
  const onChangeArticleList = useEventCallback((value: ItemVM<MotArtCitizenship> | null) => {
    onChange('article', value);
  });

  const onChangeForeign = useEventCallback(
    (key: keyof ForeignState, value: ForeignState[keyof ForeignState]) => {
      const newValue = {
        ...values?.foreign,
        [key]: value,
      };

      onChange('foreign', newValue);
    }
  );

  const onChangeItalian = useEventCallback(
    (key: keyof ItalianState, value: ItalianState[keyof ItalianState]) => {
      const newValue = {
        ...values?.italian,
        [key]: value,
      };

      onChange('italian', newValue);
    }
  );

  const onChangesItalian = useEventCallback((newValueItalian?: ItalianState) => {
    onChange('italian', newValueItalian);
  });

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Data acquisto cittadinanza *"
          aria-label="Data acquisto cittadinanza"
          value={values?.dateBuying || null}
          onChange={(date) => {
            onChange('dateBuying', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              id="dateBuying"
              {...params}
              fullWidth
              error={touched?.dateBuying && Boolean(errors?.dateBuying)}
              helperText={errors?.dateBuying}
              disabled={disabled}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <ArticleList
          value={values?.article?.id?.toString() || ''}
          onChange={onChangeArticleList}
          idReason={idReason}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Dichiarazione resa il *"
          aria-label="Dichiarazione resa il"
          value={values?.dateOath || null}
          onChange={(date) => {
            onChange('dateOath', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              id="dateOath"
              {...params}
              fullWidth
              error={touched?.dateOath && Boolean(errors?.dateOath)}
              helperText={errors?.dateOath}
              disabled={disabled}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={1} style={{ display: 'flex', alignItems: 'center' }}>
        IN
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeign"
            defaultValue={false}
            value={values?.isForeign}
            onChange={() =>
              onChangeValues({
                isForeign: !values?.isForeign,
                foreign: undefined,
                italian: undefined,
              })
            }
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeign ? (
        <Foreign onChange={onChangeForeign} values={values?.foreign} disabled={disabled} required />
      ) : (
        <Italian
          onChange={onChangeItalian}
          onChanges={onChangesItalian}
          values={values?.italian}
          dateFilter={values?.dateOath}
          disabled={disabled}
          required
        />
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <DocumentActForm
        values={values}
        onChange={(key, value) => {
          onChange(key, value);
        }}
        onChanges={(fields) => {
          onChangeValues(fields);
        }}
        errors={errors}
        touched={touched}
        requiredDigital={{
          documentDate: false,
        }}
        disabled={disabled}
      />
    </Grid>
  );
};

ChoiceItalianComponent.displayName =
  'Form Accordion Registration Citizenship - to ChoiceItalian component';

const ChoiceItalian = memo(ChoiceItalianComponent);

export default ChoiceItalian;
